import {
  type ChannelStats,
  DUPLICATE_MATCH_LABELS,
  type DuplicateArrival,
  type DuplicateDocRef,
  SOURCE_CHANNEL_LABELS,
  SOURCE_CHANNEL_ORDER,
  SOURCE_CHANNEL_UNITS,
  SOURCE_DASHBOARD_PERIODS,
  type SourceChannel,
  type SourceDashboardPeriod,
  type SourceStats,
  type SourcesDashboard,
} from '@hoopoe/shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Copy, Inbox, Lock } from 'lucide-react';
import { useState } from 'react';
import { DataNumber, DataText } from '../components/data/data-text';
import { EmptyState } from '../components/guidance/empty-state';
import { ErrorState } from '../components/guidance/error-state';
import { FieldHelp } from '../components/guidance/field-help';
import { LearnMore } from '../components/guidance/learn-more';
import { Tip } from '../components/guidance/tip';
import { useScreenTour } from '../components/guidance/tour';
import { PageHeader } from '../components/layout/app-shell';
import { when } from '../components/scrapers/monitor-status';
import { api } from '../lib/api';

// Admin → Sources (task 42): what each source channel (Upload page, ingest API, website monitors, social feeds) and
// each of its sources brought in over a period, how much was already in Hoopoe (the same file, or the same text in
// another file, from any stream) and what failed. Duplicates are stored and extracted once, whichever stream
// brings them; this page shows where they came from.

export const Route = createFileRoute('/admin/sources')({ component: SourcesPage });

const TOUR = {
  id: 'admin-sources',
  steps: [
    {
      target: '[data-tour="sources-channels"]',
      title: 'Four ways in',
      body: 'Files come in through the Upload page, the ingest API, website monitors and social feeds. Each card counts what its channel brought in.',
      learnMore: 'source-dashboards',
    },
    {
      target: '[data-tour="sources-duplicates"]',
      title: 'Kept once',
      body: 'Something that was already in Hoopoe, from any channel, is kept once. Automated copies with the same text are kept in the Library only, so nothing is extracted twice.',
    },
  ],
};

const MANAGE = {
  manual: { to: '/upload', label: 'Upload page' },
  api: { to: '/admin/api-keys', label: 'API keys' },
  scraper: { to: '/admin/scrapers', label: 'Website monitors' },
  social: { to: '/admin/social', label: 'Social feeds' },
} as const satisfies Record<SourceChannel, { to: string; label: string }>;

/** Where a source is managed: its monitor's page, or its channel's Admin screen. */
function SourceName({ s }: { s: SourceStats }) {
  const name = <DataText>{s.name}</DataText>;
  if (!s.link) return <span className="font-medium">{name}</span>;
  if (s.channel === 'scraper')
    return (
      <Link to="/admin/scrapers/$id" params={{ id: s.sourceId }} className="font-medium underline">
        {name}
      </Link>
    );
  return (
    <Link to={MANAGE[s.channel].to} className="font-medium underline">
      {name}
    </Link>
  );
}

const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

function SourcesPage() {
  useScreenTour(TOUR);
  const [days, setDays] = useState<SourceDashboardPeriod>(30);
  const q = useQuery({
    queryKey: ['admin', 'sources', days],
    queryFn: () => api<SourcesDashboard>(`/admin/sources?days=${days}`),
    placeholderData: prev => prev,
  });

  if (q.isError)
    return (
      <>
        <PageHeader title="Sources" />
        <ErrorState error={q.error} title="The sources couldn’t be loaded" onRetry={() => q.refetch()} learnMore="source-dashboards" />
      </>
    );
  const d = q.data;

  return (
    <div className="space-y-8">
      <PageHeader
        title="Sources"
        description="What each way in brought to Hoopoe — the Upload page, the ingest API, website monitors and social feeds — and how much of it was already here."
      />
      <Tip id="sources-dedupe" learnMore="source-dashboards">
        The same file is stored once, whichever channel brings it. A file with the same text as one already here (a DOCX and its PDF, a page
        a monitor captured and a program also sent) is linked to it; when it came in automatically, it is kept in the Library only.
      </Tip>

      <fieldset className="flex flex-wrap items-center gap-2">
        <legend className="sr-only">Period</legend>
        <span className="text-sm text-muted-foreground" aria-hidden="true">
          Period:
        </span>
        {SOURCE_DASHBOARD_PERIODS.map(p => (
          <button
            key={p}
            type="button"
            aria-pressed={days === p}
            onClick={() => setDays(p)}
            className="h-8 rounded-md border px-3 text-sm aria-pressed:border-foreground aria-pressed:bg-primary aria-pressed:text-primary-foreground"
          >
            Last {p} days
          </button>
        ))}
      </fieldset>

      {!d ? (
        <div className="space-y-3" aria-busy="true">
          <span className="sr-only">Loading…</span>
          <div className="h-32 animate-pulse rounded-lg bg-muted" />
          <div className="h-64 animate-pulse rounded-lg bg-muted" />
        </div>
      ) : (
        <>
          <p className="text-sm" data-testid="sources-totals">
            <DataNumber>{plural(d.totals.arrivals, 'arrival')}</DataNumber> in the last {d.days} days ·{' '}
            <DataNumber>{plural(d.totals.stored, 'new document')}</DataNumber> ·{' '}
            <DataNumber>{plural(d.totals.duplicates, 'duplicate')}</DataNumber> ·{' '}
            <DataNumber>{d.totals.failed.toLocaleString()}</DataNumber> failed processing
          </p>

          <section aria-labelledby="channels-title" className="space-y-3" data-tour="sources-channels">
            <h2 id="channels-title" className="font-semibold">
              Channels
            </h2>
            <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {d.channels.map(c => (
                <ChannelCard key={c.channel} c={c} />
              ))}
            </ul>
          </section>

          <Daily d={d} />

          <section aria-labelledby="sources-title" className="space-y-3">
            <h2 id="sources-title" className="font-semibold">
              By source
            </h2>
            {d.sources.length === 0 ? (
              <EmptyState icon={Inbox} title="Nothing has come in yet" learnMore="source-dashboards">
                Upload a file, or set up an API key, a website monitor or a social feed.
              </EmptyState>
            ) : (
              <SourcesTable sources={d.sources} />
            )}
          </section>

          <section aria-labelledby="dupes-title" className="space-y-3" data-tour="sources-duplicates">
            <h2 id="dupes-title" className="flex items-center gap-1 font-semibold">
              Recent duplicates
              <FieldHelp
                label="Duplicates"
                help="Same file: the exact bytes were already stored, so nothing new was kept. Same text: a different file whose text is (almost) all in an earlier document; it is kept and linked to it."
                learnMore="source-dashboards"
              />
            </h2>
            {d.duplicates.length === 0 ? (
              <EmptyState icon={Copy} title={`No duplicates in the last ${d.days} days`}>
                When something that is already in Hoopoe comes in again, from any channel, it shows here.
              </EmptyState>
            ) : (
              <DuplicatesTable rows={d.duplicates} />
            )}
          </section>
        </>
      )}
      <LearnMore slug="source-dashboards" />
    </div>
  );
}

function ChannelCard({ c }: { c: ChannelStats }) {
  const unit = SOURCE_CHANNEL_UNITS[c.channel];
  const manage = MANAGE[c.channel];
  return (
    <li
      className="space-y-2 rounded-lg border bg-surface p-4"
      aria-labelledby={`channel-${c.channel}`}
      data-testid={`channel-${c.channel}`}
    >
      <h3 id={`channel-${c.channel}`} className="font-medium">
        {c.label}
      </h3>
      <p>
        <DataNumber className="text-2xl font-semibold">{c.arrivals.toLocaleString()}</DataNumber>{' '}
        <span className="text-sm text-muted-foreground">{c.arrivals === 1 ? 'arrival' : 'arrivals'}</span>
      </p>
      <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-0.5 text-sm">
        <dt className="text-muted-foreground">New documents</dt>
        <dd>
          <DataNumber>{c.stored.toLocaleString()}</DataNumber>
        </dd>
        <dt className="text-muted-foreground">Already here</dt>
        <dd>
          <DataNumber>{c.duplicates.toLocaleString()}</DataNumber>
          {c.duplicates > 0 && (
            <span className="block text-xs text-muted-foreground">
              {c.exact} same file · {c.near} same text
            </span>
          )}
        </dd>
        <dt className="text-muted-foreground">Failed processing</dt>
        <dd className={c.failed ? 'text-danger-foreground' : ''}>
          <DataNumber>{c.failed.toLocaleString()}</DataNumber>
        </dd>
        <dt className="text-muted-foreground">Last arrival</dt>
        <dd>
          <DataNumber>{c.lastArrivalAt ? when(c.lastArrivalAt) : 'None'}</DataNumber>
        </dd>
      </dl>
      <p className="text-xs text-muted-foreground">
        {c.configuredSources === null
          ? `${plural(c.activeSources, unit, 'people')} uploaded`
          : `${c.activeSources.toLocaleString()} of ${plural(c.configuredSources, unit)} brought something in`}{' '}
        ·{' '}
        <Link to={manage.to} className="underline">
          {manage.label}
        </Link>
      </p>
    </li>
  );
}

/** Arrivals per day: one grey bar per day (duplicates in the lighter part), with the numbers as a table. */
function Daily({ d }: { d: SourcesDashboard }) {
  const totals = d.daily.map(x => SOURCE_CHANNEL_ORDER.reduce((n, c) => n + x.counts[c], 0));
  const max = Math.max(1, ...totals);
  const busiest = d.daily[totals.indexOf(Math.max(...totals))];
  return (
    <section aria-labelledby="daily-title" className="space-y-3">
      <h2 id="daily-title" className="font-semibold">
        Arrivals per day
      </h2>
      <div
        role="img"
        aria-label={`Arrivals per day over the last ${d.days} days: ${d.totals.arrivals} in all${busiest && max > 0 ? `, most on ${busiest.day} (${max})` : ''}.`}
        className="flex h-28 items-end gap-px rounded-lg border bg-surface p-2"
      >
        {d.daily.map((x, i) => {
          const total = totals[i] ?? 0;
          const dup = Math.min(x.duplicates, total);
          return (
            <div key={x.day} className="flex h-full min-w-0 flex-1 flex-col justify-end" title={`${x.day}: ${total} (${dup} already here)`}>
              <div className="w-full bg-muted-foreground" style={{ height: `${(dup / max) * 100}%` }} />
              <div className="w-full bg-foreground" style={{ height: `${((total - dup) / max) * 100}%` }} />
            </div>
          );
        })}
      </div>
      <p className="flex flex-wrap gap-4 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <span className="inline-block size-2.5 bg-foreground" aria-hidden="true" /> New
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block size-2.5 bg-muted-foreground" aria-hidden="true" /> Already here
        </span>
      </p>
      <details className="text-sm">
        <summary className="cursor-pointer text-muted-foreground">Show the numbers</summary>
        <section
          className="mt-2 max-h-64 overflow-auto rounded-lg border bg-surface"
          // biome-ignore lint/a11y/noNoninteractiveTabindex: the scrollable table must be reachable by keyboard
          tabIndex={0}
          aria-label="Arrivals per day, by channel"
        >
          <table className="w-full text-sm" aria-label="Arrivals per day">
            <thead>
              <tr className="border-b text-muted-foreground">
                <th className="p-2 text-start font-medium">Day</th>
                {SOURCE_CHANNEL_ORDER.map(c => (
                  <th key={c} className="p-2 text-end font-medium">
                    {SOURCE_CHANNEL_LABELS[c]}
                  </th>
                ))}
                <th className="p-2 text-end font-medium">Already here</th>
              </tr>
            </thead>
            <tbody>
              {[...d.daily].reverse().map(x => (
                <tr key={x.day} className="border-b last:border-0">
                  <td className="p-2">
                    <DataNumber>{x.day}</DataNumber>
                  </td>
                  {SOURCE_CHANNEL_ORDER.map(c => (
                    <td key={c} className="p-2 text-end">
                      <DataNumber>{x.counts[c]}</DataNumber>
                    </td>
                  ))}
                  <td className="p-2 text-end">
                    <DataNumber>{x.duplicates}</DataNumber>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </details>
    </section>
  );
}

const STATE_NOTE: Record<SourceStats['state'], string | null> = { active: null, off: 'Off', removed: 'Removed' };

function SourcesTable({ sources }: { sources: SourceStats[] }) {
  return (
    <div className="relative overflow-x-auto rounded-lg border bg-surface">
      <table className="w-full text-sm" aria-label="Sources">
        <thead>
          <tr className="border-b text-muted-foreground">
            <th className="p-2 text-start font-medium">Source</th>
            <th className="p-2 text-start font-medium">Channel</th>
            <th className="p-2 text-end font-medium">Arrivals</th>
            <th className="p-2 text-end font-medium">New</th>
            <th className="p-2 text-end font-medium">Already here</th>
            <th className="p-2 text-end font-medium">Failed</th>
            <th className="p-2 text-start font-medium">Last arrival</th>
          </tr>
        </thead>
        <tbody>
          {sources.map(s => (
            <tr key={`${s.channel}:${s.sourceId}`} className="border-b align-top last:border-0" aria-label={s.name}>
              <td className="max-w-xs p-2">
                <SourceName s={s} />
                {STATE_NOTE[s.state] && <span className="ms-1 text-xs text-muted-foreground">({STATE_NOTE[s.state]})</span>}
                {s.lastError && (
                  <span className="block text-xs text-danger-foreground">
                    <DataText>{s.lastError}</DataText>
                  </span>
                )}
              </td>
              <td className="p-2">{SOURCE_CHANNEL_LABELS[s.channel]}</td>
              <td className="p-2 text-end">
                <DataNumber>{s.arrivals.toLocaleString()}</DataNumber>
              </td>
              <td className="p-2 text-end">
                <DataNumber>{s.stored.toLocaleString()}</DataNumber>
              </td>
              <td className="p-2 text-end" data-testid="source-duplicates">
                <DataNumber>{s.duplicates.toLocaleString()}</DataNumber>
              </td>
              <td className={`p-2 text-end ${s.failed ? 'text-danger-foreground' : ''}`}>
                <DataNumber>{s.failed.toLocaleString()}</DataNumber>
              </td>
              <td className="p-2">
                <DataNumber>{s.lastArrivalAt ? when(s.lastArrivalAt) : 'None'}</DataNumber>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DocRef({ doc }: { doc: DuplicateDocRef }) {
  return (
    <>
      <Link to="/library/$id" params={{ id: doc.id }} className="inline-flex items-center gap-1 underline">
        {doc.confidential && <Lock className="size-3.5 shrink-0" aria-label="Confidential" />}
        <DataText>{doc.title}</DataText>
      </Link>
      <span className="block text-xs text-muted-foreground">{SOURCE_CHANNEL_LABELS[doc.channel]}</span>
    </>
  );
}

function DuplicatesTable({ rows }: { rows: DuplicateArrival[] }) {
  return (
    <div className="relative overflow-x-auto rounded-lg border bg-surface">
      <table className="w-full text-sm" aria-label="Recent duplicates">
        <thead>
          <tr className="border-b text-muted-foreground">
            <th className="p-2 text-start font-medium">When</th>
            <th className="p-2 text-start font-medium">Came in through</th>
            <th className="p-2 text-start font-medium">Match</th>
            <th className="p-2 text-start font-medium">Kept as</th>
            <th className="p-2 text-start font-medium">Already here as</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} className="border-b align-top last:border-0" data-testid="duplicate-row">
              <td className="p-2">
                <DataNumber>{when(r.arrivedAt)}</DataNumber>
              </td>
              <td className="p-2">
                {SOURCE_CHANNEL_LABELS[r.channel]}
                {r.sourceName && (
                  <span className="block text-xs text-muted-foreground">
                    <DataText>{r.sourceName}</DataText>
                  </span>
                )}
              </td>
              <td className="p-2">
                {DUPLICATE_MATCH_LABELS[r.match]}
                {r.similarity !== null && (
                  <span className="text-muted-foreground">
                    {' '}
                    (<DataNumber>{r.similarity}</DataNumber>%)
                  </span>
                )}
              </td>
              <td className="max-w-xs p-2">
                {r.match === 'exact' ? <span className="text-muted-foreground">Not stored again</span> : <DocRef doc={r.document} />}
              </td>
              <td className="max-w-xs p-2">
                <DocRef doc={r.original} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
